import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { DirectionService } from '../../direction.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgForm } from '@angular/forms';
import { FilesService } from '../../main-section/printing-table/file.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector: 'app-transfer-files-to-user',
  templateUrl: './transfer-files-to-user.component.html',
  styleUrls: ['./transfer-files-to-user.component.css'],
  host: {
    class: 'fill-screen-modal'
  }
})
export class TransferFilesToUserComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;
  isLoading: boolean = false;

  files: any[] = [];
  printingService: string = '';
  serverAddress: string = '';
  expressServerAddress: string = 'https://img-express.eazix.io';
  imageErrors: { [fileIndex: number]: { [imageIndex: number]: boolean } } = {};
  userEmail: string = '';
  printerOrBranchID: string = '';

  constructor(
    private directionService: DirectionService,
    private dialogService: DialogService,
    private filesService: FilesService,
    private _snackBar: MatSnackBar,
    private translate: TranslateService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (this.data) {
      if (this.data.files) {
        this.files = this.data.files;
      }
      if (this.data.printingService) {
        this.printingService = this.data.printingService;
      }
      if (this.data.serverAddress) {
        this.serverAddress = this.data.serverAddress;
      }
      if (this.data.expressServerAddress) {
        this.expressServerAddress = this.data.expressServerAddress;
      }
      if (this.data.printerOrBranchID) {
        this.printerOrBranchID = this.data.printerOrBranchID;
      }
    }
  }

  async ngOnInit() {
    this.isLoading = true;
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    // Initialize image errors object
    this.files.forEach((file, fileIndex) => {
      this.imageErrors[fileIndex] = {};
      file.images?.forEach((image, imageIndex) => {
        this.imageErrors[fileIndex][imageIndex] = false;
      });
    });

    this.isLoading = false;
  }

  closeTransferFilesToUserDialog() {
    this.dialogService.onCloseTransferFilesToUserDialog();
  }

  getDisplayFileNameAfterSplit(file: any): string {
    if (!file) {
      return '';
    }
    // If originalFileName exists, return it as is
    if (file.originalFileName && file.originalFileName.trim() !== '') {
      return file.originalFileName;
    }
    // For fileName, use the split logic
    if (!file.fileName) {
      return '';
    }
    const parts = file.fileName.split('-');
    return parts.length > 1 ? parts[1] : file.fileName;
  }

  getUserName(): string {
    return localStorage.getItem('userName') || '';
  }

  // Get userName for file - use originalUserName if file was transferred
  getUserNameForFile(file: any): string {
    if (file && file.originalUserName && file.originalUserName.trim() !== '') {
      return file.originalUserName;
    }
    return this.getUserName();
  }

  getImgPlotter(file: any, imageIndex: number): string {
    if (!file || !file.images || !file.images[imageIndex]) {
      return '';
    }
    const thumbnail = file.images[imageIndex].thumbnailPath;
    if (!thumbnail) {
      return '';
    }
    const isNewThumbnail =
      thumbnail.startsWith('uploads') ||
      thumbnail.startsWith('/home/ubuntu/IMG-Plotter');
    let newThumbnail = '';
    if (isNewThumbnail) {
      if (thumbnail.startsWith('uploads')) {
        // Use originalUserName if file was transferred
        newThumbnail = this.serverAddress + '/uploads/' + this.getUserNameForFile(file) + '/' + thumbnail.split('/').pop();
      } else {
        const shortThumbnail = thumbnail.replace(
          '/home/ubuntu/IMG-Plotter',
          ''
        );
        newThumbnail = this.serverAddress + shortThumbnail;
      }
    } else {
      newThumbnail = thumbnail;
    }
    return newThumbnail;
  }

  getImgExpress(file: any, imageIndex: number): string {
    if (!file || !file.images || !file.images[imageIndex]) {
      return '';
    }
    const image = file.images[imageIndex];
    const thumbnail = image.thumbnailPath;
    
    if (!thumbnail) {
      return image.path || '';
    }
    
    // In printing table, express thumbnails are processed and contain full URLs like:
    // https://img-express.eazix.io/uploads/yaron@eazix.io/thumbnail-xxx.jpg
    // Same logic as in printing-table.component.html:
    // serverAddress+'/uploads/' + getUserNameForFile(file) + '/' + currentImage.thumbnailPath.split('/').pop()
    
    // Always extract just the filename (last part after /) and rebuild
    // This handles both full URLs and relative paths
    const filename = thumbnail.split('/').pop();
    const originalUserName = this.getUserNameForFile(file);
    
    // Build the URL exactly like in printing table
    // Make sure expressServerAddress doesn't have trailing slash
    const serverAddress = this.expressServerAddress.endsWith('/') 
      ? this.expressServerAddress.slice(0, -1) 
      : this.expressServerAddress;
    
    return serverAddress + '/uploads/' + originalUserName + '/' + filename;
  }

  getImageSrc(file: any, imageIndex: number): string {
    if (this.printingService === 'plotter') {
      return this.getImgPlotter(file, imageIndex);
    } else {
      return this.getImgExpress(file, imageIndex);
    }
  }

  onImageError(fileIndex: number, imageIndex: number) {
    if (!this.imageErrors[fileIndex]) {
      this.imageErrors[fileIndex] = {};
    }
    this.imageErrors[fileIndex][imageIndex] = true;
  }

  onTransferFiles() {
    if (!this.userEmail || this.userEmail.length < 3) {
      return;
    }

    if (!this.printerOrBranchID) {
      console.error('printerOrBranchID is missing');
      return;
    }

    // Extract file IDs
    const fileIds = this.files.map(file => file._id).filter(id => id);

    if (fileIds.length === 0) {
      console.error('No file IDs found');
      return;
    }

    this.isLoading = true;

    // Get current user ID (the user performing the transfer)
    const currentUserId = this.authService.getUserId();

    this.filesService.transferFilesToUser(
      this.printingService,
      this.printerOrBranchID,
      this.userEmail,
      fileIds,
      currentUserId
    ).subscribe({
      next: (response) => {
        this.isLoading = false;
        const successMessage = this.translate.instant('su-management.transfer-files-to-user.success');
        this._snackBar.open(successMessage, '', {
          duration: 5000,
          verticalPosition: 'top'
        });
        this.closeTransferFilesToUserDialog();
      },
      error: (error) => {
        console.error('Error transferring files:', error);
        this.isLoading = false;
        const errorMessage = this.translate.instant('su-management.transfer-files-to-user.error');
        this._snackBar.open(errorMessage, '', {
          duration: 5000,
          verticalPosition: 'top'
        });
      }
    });
  }

  ngOnDestroy() {
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
  }
}

